import { FC, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography
} from "@mui/material";
import { useWarehouseLookup } from "./useWarehouseLookup";
import {
  HubSyncValues,
  SyncEntityType,
  SyncRow,
  SyncSource,
  SyncableField,
  buildPatch,
  buildSyncRows,
  isActionable
} from "./utils";

type Props = {
  open: boolean;
  onClose: () => void;
  /** The id entered next to the sync button in the lifecycle popup. */
  catalystId: string;
  /** Which book this entity is in: the credit limit is read differently for brokers. */
  entityType: SyncEntityType;
  hubValues: HubSyncValues;
  /**
   * False when this entity has no credit fields to write into, so the three risk rows are shown as
   * unsyncable. Countries pass false for every party but the TSO and the exchange. See
   * SyncRowOptions.
   */
  supportsCredit?: boolean;
  onApply: (patch: Partial<HubSyncValues>) => void;
};

const SOURCE_LABELS: Record<SyncSource, string> = {
  compliance: "Compliance",
  risk: "Credit risk"
};

const SOURCE_EMPTY: Record<SyncSource, string> = {
  compliance: "No compliance record exists for this id.",
  risk: "No credit assessment exists for this id."
};

const Row: FC<{
  row: SyncRow;
  checked: boolean;
  onToggle: () => void;
}> = ({ row, checked, onToggle }) => {
  const actionable = isActionable(row);

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={2}
      sx={{ opacity: actionable ? 1 : 0.6 }}
    >
      <Box sx={{ width: 40 }}>
        {actionable ? (
          <Checkbox checked={checked} onChange={onToggle} size="small" />
        ) : null}
      </Box>

      <Typography variant="body2" sx={{ flex: 1 }}>
        {row.label}
      </Typography>

      <Typography variant="body2" sx={{ flex: 1 }} color="text.secondary">
        {row.hubDisplay}
      </Typography>

      <Typography variant="body2" sx={{ width: 24 }} color="text.secondary">
        {actionable ? "->" : ""}
      </Typography>

      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Typography variant="body2">{row.warehouseDisplay}</Typography>
        {/* The dialog has room for the whole sentence, so it shows that rather than the table's
            short label. Coloured only when the row actually needs somebody to decide something. */}
        {!row.syncable && row.unsyncable ? (
          <Typography
            variant="caption"
            color={
              row.unsyncable.kind === "attention"
                ? "warning.main"
                : "text.secondary"
            }
          >
            {row.unsyncable.reason}
          </Typography>
        ) : null}
        {row.syncable && !row.differs ? (
          <Typography variant="caption" color="text.secondary">
            Already in sync
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

export const WarehouseSyncDialog: FC<Props> = ({
  open,
  onClose,
  catalystId,
  entityType,
  hubValues,
  supportsCredit = true,
  onApply
}) => {
  const [accepted, setAccepted] = useState<Set<SyncableField>>(new Set());

  // Only query while the dialog is open, so editing the id in the popup does not fire a request
  // per keystroke.
  const lookup = useWarehouseLookup(open ? catalystId.trim() : undefined);

  const rows = lookup.data
    ? buildSyncRows(lookup.data, hubValues, entityType, { supportsCredit })
    : [];
  const actionableRows = rows.filter(isActionable);

  // Pre-tick every real difference. Nothing is written until the user confirms, and unticking is
  // less work than hunting for the rows that matter. Reopening re-ticks too, so a cached lookup
  // does not bring back whatever was unticked last time.
  useEffect(() => {
    setAccepted(new Set(actionableRows.map((row) => row.field)));
    // Re-run when a new lookup lands or the dialog reopens, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookup.data, open]);

  const toggle = (field: SyncableField) => {
    setAccepted((prev) => {
      const next = new Set(prev);
      if (next.has(field)) {
        next.delete(field);
      } else {
        next.add(field);
      }
      return next;
    });
  };

  const selectedCount = actionableRows.filter((row) =>
    accepted.has(row.field)
  ).length;

  const renderSection = (source: SyncSource, available: boolean) => {
    const sectionRows = rows.filter((row) => row.source === source);

    return (
      <Box display="flex" flexDirection="column" gap={1}>
        <Typography variant="subtitle2">{SOURCE_LABELS[source]}</Typography>
        {available ? (
          sectionRows.map((row) => (
            <Row
              key={row.field}
              row={row}
              checked={accepted.has(row.field)}
              onToggle={() => toggle(row.field)}
            />
          ))
        ) : (
          <Alert severity="info">{SOURCE_EMPTY[source]}</Alert>
        )}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Differences against the data warehouse</DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} paddingTop={1}>
          <Typography variant="caption" color="text.secondary">
            {catalystId.trim()}
          </Typography>

          {lookup.isFetching ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={18} />
              <Typography variant="body2">Reading the warehouse...</Typography>
            </Box>
          ) : null}

          {lookup.isError && !lookup.isFetching ? (
            <Alert severity="error">
              Nothing found for that id, or the warehouse could not be reached.
              Check the id and try again.
            </Alert>
          ) : null}

          {lookup.data && !lookup.isFetching ? (
            <>
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="caption" sx={{ width: 40 }} />
                <Typography variant="caption" sx={{ flex: 1 }}>
                  Field
                </Typography>
                <Typography variant="caption" sx={{ flex: 1 }}>
                  In the Hub
                </Typography>
                <Typography variant="caption" sx={{ width: 24 }} />
                <Typography variant="caption" sx={{ flex: 1 }}>
                  In the warehouse
                </Typography>
              </Box>
              <Divider />

              {renderSection(
                "compliance",
                !!lookup.data.complianceDataAvailable
              )}
              {renderSection("risk", !!lookup.data.riskDataAvailable)}

              {actionableRows.length === 0 ? (
                <Alert severity="success">
                  The Hub already matches the warehouse. Nothing to accept.
                </Alert>
              ) : null}
            </>
          ) : null}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={() => onApply(buildPatch(rows, accepted))}
          variant="contained"
          disabled={selectedCount === 0 || lookup.isFetching}
        >
          {`Accept ${selectedCount} change${selectedCount === 1 ? "" : "s"}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
